"use client";

import Link from "next/link";
import { motion } from "motion/react";
import { useState } from "react";

import { AddToCartButton } from "@/components/catalog/add-to-cart-button";
import { AvailabilityLabel } from "@/components/catalog/availability-label";
import { ProductImage } from "@/components/catalog/product-image";
import { ProductPriceBlock } from "@/components/catalog/product-price-block";
import { QuantityStepper } from "@/components/catalog/quantity-stepper";
import { useSubtleInteraction } from "@/components/ui/motion-primitives";
import type { PublicCatalogProduct } from "@/domain/catalog/catalog-product";
import {
  findFirstMatchingModelIndex,
  getSearchTokens,
  getVisibleCompatibleModels,
  type VisibleCompatibleModelsItem,
} from "@/lib/catalog/compatible-models";
import { formatProductType } from "@/lib/catalog/product-type";

const MotionLink = motion.create(Link);

export interface ProductListCardProps {
  readonly product: PublicCatalogProduct;
  readonly loading?: "eager" | "lazy";
  readonly priority?: boolean;
  readonly searchQuery?: string;
}

export function ProductListCard({
  loading = "lazy",
  priority = false,
  product,
  searchQuery,
}: Readonly<ProductListCardProps>) {
  const [quantity, setQuantity] = useState(1);
  const interaction = useSubtleInteraction();
  const compatibleModelItems = getCompatibleModelItems(
    product.compatibleModels,
    searchQuery,
  );

  return (
    <article className="[contain-intrinsic-size:auto_320px] [content-visibility:auto] flex min-h-full flex-col rounded-lg border border-outline-variant bg-surface p-2 shadow-sm">
      <div>
        <MotionLink
          aria-label={`Ver ${product.shortName ?? product.name}`}
          className="flex aspect-square items-center justify-center overflow-hidden rounded-md bg-white p-1.5 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          href={`/productos/${product.slug}`}
          prefetch={false}
          {...interaction}
        >
          <ProductImage
            alt={product.name}
            loading={loading}
            imageUrl={product.technicalSheetImageUrl ?? product.primaryImageUrl}
            priority={priority}
            sizes="(max-width: 640px) calc((100vw - 44px) / 2), (max-width: 768px) calc((100vw - 56px) / 3), 240px"
          />
        </MotionLink>

        <div className="mt-2 min-w-0">
          <Link
            className="line-clamp-2 min-h-10 rounded-sm text-sm font-extrabold leading-5 text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            href={`/productos/${product.slug}`}
            prefetch={false}
          >
            {product.shortName ?? product.name}
          </Link>
          <p className="mt-0.5 line-clamp-1 text-xs font-bold text-primary">
            {compatibleModelItems.length > 0
              ? compatibleModelItems.map((item, index) => (
                  <CompatibleModelTextItem
                    item={item}
                    key={`${item.kind}-${item.value}-${index}`}
                    showSeparator={index > 0}
                  />
                ))
              : product.name}
          </p>
          <p className="mt-1 text-xs text-muted">
            {formatProductType(product.type)}
            {product.brandName ? ` - ${product.brandName}` : ""}
          </p>
          <AvailabilityLabel availability={product.availability} />
          <ProductPriceBlock
            currencyCode={product.currencyCode}
            salePrice={product.salePrice}
            wholesaleMinQuantity={product.wholesaleMinQuantity}
            wholesalePrice={product.wholesalePrice}
          />
        </div>
      </div>

      <div className="mt-auto grid gap-2 border-t border-surface-container-high pt-2">
        <QuantityStepper onChange={setQuantity} value={quantity} />
        <AddToCartButton product={product} quantity={quantity} />
      </div>
    </article>
  );
}

function getCompatibleModelItems(
  models: readonly string[],
  searchQuery: string | undefined,
): VisibleCompatibleModelsItem[] {
  const tokens = getSearchTokens(searchQuery);
  const matchIndex = findFirstMatchingModelIndex(models, tokens);

  return getVisibleCompatibleModels(models, matchIndex);
}

function CompatibleModelTextItem({
  item,
  showSeparator,
}: Readonly<{
  item: VisibleCompatibleModelsItem;
  showSeparator: boolean;
}>) {
  return (
    <>
      {showSeparator ? ", " : null}
      {item.kind === "ellipsis" ? (
        item.value
      ) : (
        <span
          className={
            item.isMatch
              ? "bg-primary-fixed px-0.5 text-primary"
              : undefined
          }
        >
          {item.value}
        </span>
      )}
    </>
  );
}
